import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import _ from "lodash";

import CampaignComponents from "./Setup/CampaignComponents";
import AssignCampaignsDropDown from "./CampaignAssignment/AssignCampaignsDropDown";
import LiveScreen from "./Setup/LiveScreen";
import Layout from "../../Layout/Layout";
import Rotate from "./Setup/Rotate";

import { useGetCampaignContent, useGetCampaigns, useGetDevice, useSendActionDevice, useRestartDevice, useNotificationDeviceOff, useMuteUnmuteDevice } from "../../../queries";
import { setPaused, setSelectedDevice } from "../../../redux/Slices/deviceSlice";
import { toggleDialog } from "../../../redux/Slices/Global/globalSlice";
import { useTitle } from "../../../config/global";
import { resolveDeviceOnline, resolveDeviceStatus } from "../../../utils";
import FirebaseDeviceListener from "../../Services/FirebaseDeviceListener";
import ContentPreview from "./Preview/ContentPreview";
import DeviceLastLoginListener from "../../Services/DeviceLastLoginListener";
import moment from "moment/moment";
import { getDownloadURL, ref, getMetadata } from "firebase/storage";
import { storage, authReady } from "../../../config/firebase";
import { getScreenshotBaseline, pollForNewScreenshot, withCacheBust } from "../../../utils/screenshot";
import { ProgressSpinner } from "primereact/progressspinner";
import AnalyticsDialog from "./Setup/AnalyticsDialog";
import WorkingHours from "./Setup/WorkingHours";
import DeviceSettings from "./DeviceSettings";
import { Button } from "../../../components/ui/Button";
import { tzMismatch, tzDriftPhrase } from "../../../utils/timezone";
import EffectiveTimezone from "../../../components/ui/EffectiveTimezone";
import { useScreenHealth, useScreenDayLog } from "../../../queries/screenHealth";
import MergedScreen from "./Health/MergedScreen";
import SchedulePausedChip from "./Grouping/SchedulePausedChip";
import { clock, titleText, resolveMuted } from "./Health/model";
import { hoursWindow } from "../../../utils/deviceSettingsRead";

const BlackoutScreen = ({ message }) => {
  const { t } = useTranslation();
  return (
    <div className="bg-black w-full h-full flex flex-col items-center justify-center gap-4 relative overflow-hidden">
      <div className="noise-panel opacity-5"></div>
      <div className="relative z-10 flex flex-col items-center gap-6">
        <div className="w-16 h-16 rounded-full border-4 border-white/10 border-t-white/40 animate-spin shadow-[0_0_30px_rgba(255,255,255,0.1)]"></div>
        <h1 className="text-white/80 text-xl font-black tracking-[0.3em] uppercase flicker">{message || t('devices.status_internal_process')}</h1>
      </div>
    </div>
  );
};

const GrayScreen = ({ message }) => (
  <div className="bg-slate-900 w-full h-full flex flex-col items-center justify-center gap-4 relative overflow-hidden">
    <div className="noise-panel opacity-10"></div>
    <div className="relative z-10 flex flex-col items-center gap-6">
      <div className="flex gap-3">
        {[0, 0.1, 0.2].map(d => (
          <div key={d} className="w-2.5 h-10 bg-indigo-500 rounded-full animate-bounce shadow-[0_0_20px_rgba(99,102,241,0.4)]" style={{ animationDelay: `${d}s` }}></div>
        ))}
      </div>
      <h1 className="text-slate-200 text-xl font-bold tracking-tight flicker">{message}</h1>
    </div>
  </div>
);

const OfflineScreen = () => {
  const { t } = useTranslation();
  return (
  <div className="absolute inset-0 z-50 screen bg-black flex flex-col items-center justify-center overflow-hidden">
    <div className="noise-panel opacity-25"></div>
    <div className="scan-line"></div>
    
    <div className="absolute w-[150%] h-[150%] bg-[radial-gradient(circle,rgba(255,0,0,0.05)_0%,transparent_70%)] animate-pulse pointer-events-none"></div>

    <div className="relative z-10 flex flex-col items-center gap-8 transform scale-110">
      <div className="group relative">
        <div className="absolute inset-0 bg-red-600 blur-[40px] opacity-20 group-hover:opacity-40 transition-opacity animate-pulse"></div>
        <div className="w-28 h-28 rounded-[2.5rem] bg-red-950/30 flex items-center justify-center border-2 border-red-500/40 text-red-500 shadow-2xl relative z-10 flicker">
          <i className="fa-solid fa-plane-slash text-5xl"></i>
        </div>
      </div>
      
      <div className="text-center relative z-10">
        <div className="inline-block px-3 py-1 bg-red-500/10 border border-red-500/20 rounded-full mb-4">
          <span className="text-[10px] font-black text-red-500 uppercase tracking-[0.2em] animate-pulse">{t('devices.offline_connection_lost')}</span>
        </div>
        <h1 className="text-white text-4xl font-black mb-3 tracking-tighter uppercase flicker">{t('devices.offline_signal_terminated')}</h1>
        <p className="text-slate-400 text-base max-w-[320px] font-medium leading-relaxed opacity-80">
          {t('devices.offline_neural_broadcast')}
        </p>
      </div>
    </div>
  </div>
  );
};

// Standby is NOT the same as offline: the dashboard's "Turn Off" stops the
// device's REST heartbeat but deliberately leaves its RTDB connection open
// so a later "Turn On" can still reach it — the device is still genuinely
// reachable. Showing the alarming "signal lost" OfflineScreen here would be
// misleading, so standby gets its own calmer, amber-toned takeover instead.
const StandbyScreen = () => {
  const { t } = useTranslation();
  return (
  <div className="absolute inset-0 z-50 screen bg-black flex flex-col items-center justify-center overflow-hidden">
    <div className="noise-panel opacity-25"></div>

    <div className="relative z-10 flex flex-col items-center gap-8">
      <div className="w-28 h-28 rounded-[2.5rem] bg-amber-950/30 flex items-center justify-center border-2 border-amber-500/40 text-amber-500 shadow-2xl relative z-10">
        <i className="fa-solid fa-power-off text-5xl"></i>
      </div>

      <div className="text-center relative z-10">
        <div className="inline-block px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full mb-4">
          <span className="text-[10px] font-black text-amber-500 uppercase tracking-[0.2em]">{t('devices.standby_turned_off')}</span>
        </div>
        <h1 className="text-white text-4xl font-black mb-3 tracking-tighter uppercase">{t('devices.standby_title')}</h1>
        <p className="text-slate-400 text-base max-w-[320px] font-medium leading-relaxed opacity-80">
          {t('devices.standby_message')}
        </p>
      </div>
    </div>
  </div>
  );
};

// App closed locally (home/recents, short of a force-stop) — the device's
// process and RTDB socket stay alive so it's neither offline nor standby;
// nothing is on screen because the app itself isn't running. Distinct
// orange tone: unlike standby, this wasn't admin-initiated, so it's worth
// visually flagging as needing a look rather than reading as a normal state.
const AppClosedScreen = () => {
  const { t } = useTranslation();
  return (
  <div className="absolute inset-0 z-50 screen bg-black flex flex-col items-center justify-center overflow-hidden">
    <div className="noise-panel opacity-25"></div>

    <div className="relative z-10 flex flex-col items-center gap-8">
      <div className="w-28 h-28 rounded-[2.5rem] bg-orange-950/30 flex items-center justify-center border-2 border-orange-500/40 text-orange-500 shadow-2xl relative z-10">
        <i className="fa-solid fa-triangle-exclamation text-5xl"></i>
      </div>

      <div className="text-center relative z-10">
        <div className="inline-block px-3 py-1 bg-orange-500/10 border border-orange-500/20 rounded-full mb-4">
          <span className="text-[10px] font-black text-orange-500 uppercase tracking-[0.2em]">{t('devices.app_closed_badge')}</span>
        </div>
        <h1 className="text-white text-4xl font-black mb-3 tracking-tighter uppercase">{t('devices.app_closed_title')}</h1>
        <p className="text-slate-400 text-base max-w-[320px] font-medium leading-relaxed opacity-80">
          {t('devices.app_closed_message')}
        </p>
      </div>
    </div>
  </div>
  );
};

const DeviceShow = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || 'en').startsWith('ar') ? 'ar' : 'en';
  useTitle("Devices");

  const [realtimeData, setRealtimeData] = useState({
    id: "",
    name: "",
    tag: "",
    is_playing: "",
    screen_angle: "",
    is_muted: "",
    download_percentage: { campaign_id: "", percentage: 0 },
    media_playing: { currentMediaId: "", start_time: "" },
    app_version: "",
    corner: { id: "", start_time: "" },
    playground: { id: "", start_time: "" },
    banner: { id: "", start_time: "" },
  });

  const { rtdbConnected, selectedDevice } = useSelector((state) => state.device);
  const { openDialog } = useSelector((state) => state.global);

  const [currentMediaPlaying, setCurrentMediaPlaying] = useState();
  const [metadata, setMetadata] = useState(null);
  const [appVersion, setAppVersion] = useState();
  // Single source of truth for the SerialNav tab strip. Was previously two
  // entangled states (previewMode + logPreview) that drifted out of sync —
  // e.g. clicking "Live Preview" while on logs only updated previewMode and
  // left logPreview=true, so the logs panel stayed visible until the user
  // clicked the same tab twice.
  const [activeTab, setActiveTab] = useState('preview'); // 'preview' | 'screenshot' | 'logs'
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [currentCampaignId, setCurrentCampaignId] = useState("");
  const [currentCornerPlaying, setCurrentCornerPlaying] = useState();
  const [currentPlaygroundPlaying, setCurrentPlaygroundPlaying] = useState();
  const [currentBannerPlaying, setCurrentBannerPlaying] = useState();
  const [imageUrl, setImageUrl] = useState(null);
  const [error, setError] = useState(null);
  const [imgLading, setImgLading] = useState(false);
  const deviceAction = useSendActionDevice();

  const dispatch = useDispatch();
  const { id } = useParams();
  const { isLoading, data } = useGetDevice(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const health = useScreenHealth(id);
  // build 422: the day shown in «ماذا حدث اليوم» (null = today); one of the 7 strip days
  const [logDay, setLogDay] = useState(null);
  useEffect(() => { setLogDay(null); }, [id]);
  const dayLog = useScreenDayLog(id, logDay);

  // Unified-presence rollout, part 2 (2026-10-06): 100% backend-computed,
  // zero RTDB — connectivity.state is fresh by construction (same heartbeat
  // that proves the device alive carries foreground/standby), kept current
  // by useGetDevice's existing 15s poll (queries/devices.js) rather than a
  // live listener.
  const isOnline = useMemo(
    () => resolveDeviceOnline(data?.connectivity ?? selectedDevice?.connectivity, data?.online ?? selectedDevice?.online),
    [data?.connectivity, selectedDevice?.connectivity, data?.online, selectedDevice?.online]
  );
  // Distinguishes standby (dashboard "Turn Off": REST heartbeat keeps
  // running but the player soft-locked itself) and scheduled_off
  // (backend-derived, unreachable but inside the device's own configured
  // off-hours) from genuinely offline, so the full-screen takeover below
  // doesn't show an alarming "signal lost" state for an intentionally-
  // off/closed device.
  const deviceStatus = useMemo(
    () => resolveDeviceStatus(
      data?.connectivity ?? selectedDevice?.connectivity,
      data?.presence_status ?? selectedDevice?.presence_status,
      data?.online ?? selectedDevice?.online
    ),
    [data?.connectivity, selectedDevice?.connectivity, data?.presence_status, selectedDevice?.presence_status, data?.online, selectedDevice?.online]
  );

  const { isLoading: campaignLoading, data: campaignData } = useGetCampaignContent(realtimeData?.id, {
    enabled: !!realtimeData?.id,
  });

  const [selectedCampaign, setSelectedCampaign] = useState({});
  const [degree, setDegree] = useState(0);

  // Existing mutations, unchanged (same endpoints the old Controls card used): restart, turn_off/turn_on, flush.
  const restartDevice = useRestartDevice();
  const turnOnOffDevice = useNotificationDeviceOff();
  const muteUnmuteDevice = useMuteUnmuteDevice();

  const openAssignModal = useCallback(() => dispatch(toggleDialog("assignCampaignsDropDown")), [dispatch]);

  // Currently-playing / assigned campaign name for the Controls card. Trust
  // RTDB's live realtimeData.name only while RTDB is actually connected —
  // once it's not, that value can be a populated-but-stale campaign name
  // (device switched via the REST fallback and can't write RTDB), so prefer
  // the REST-computed current_campaign_name instead (also a strictly better
  // source than the old naive data?.campaigns?.[0]?.name fallback, which
  // ignored campaign type/schedule entirely).
  const isRtdbCampaignNameLive = rtdbConnected?.[selectedDevice?.serial_name] === true
    && realtimeData?.name
    && !["Prayer break", "Downloading...", "Updating..."].includes(realtimeData.name);
  const assignedCampaignName = isRtdbCampaignNameLive
    ? realtimeData.name
    : (data?.current_campaign_name || "");

  // Location = ONLY the assigned location (branch). The geo-IP guess is intentionally
  // NOT shown — it detects the relay/VPN ip ("Kansas City"), never the real venue.
  const branch = selectedDevice?.branch;
  const geoLabel = branch?.name
    ? [branch.name, [branch.city, branch.country].filter(Boolean).join(", ")].filter(Boolean).join(" — ")
    : null;
  const geoMapsUrl = (branch?.city || branch?.country)
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([branch.city, branch.country].filter(Boolean).join(", "))}`
    : null;

  // Clock check. The player fires its schedules off its OWN clock (by design —
  // it works offline), so a screen set to the wrong zone runs every schedule at
  // the wrong time and nothing else would ever show it. resolved_timezone is
  // the venue's authoritative zone (branch -> company -> Asia/Riyadh), computed
  // server-side so this never drifts from the backend's own chain.
  const clockIssue = tzMismatch(
    selectedDevice?.device_info?.timezone,
    selectedDevice?.resolved_timezone
  );

  useEffect(() => {
    dispatch(setSelectedDevice(data));
    setSelectedCampaign(() => _.isEmpty(data?.campaigns) ? "" : data?.campaigns?.[0]?.id);
    setDegree(data?.rotation || 0);
    const imageRef = ref(storage, `screenshots/${data?.serial_name}.png`);

    authReady
      .then(() => getDownloadURL(imageRef))
      .then((url) => {
        setImageUrl(url);
        return getMetadata(imageRef);
      })
      .then((meta) => {
        setMetadata(meta);
      })
      .catch((err) => {
        console.error("Failed to load screenshot:", err);
        setError("Screenshot not available");
      });
  }, [data, dispatch]);

  const refetchImg = useCallback(() => {
    if (!data?.serial_name) return;
    setIsRefreshing(true);
    setError(null);
    const imageRef = ref(storage, `screenshots/${data.serial_name}.png`);
    authReady
      .then(() => getDownloadURL(imageRef))
      .then((url) => {
        const nextUrl = withCacheBust(url); // '&t=' — a 2nd '?' corrupts the token → 403/black
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(nextUrl);
          img.onerror = reject;
          img.src = nextUrl;
        });
      })
      .then((nextUrl) => {
        setImageUrl(nextUrl);
        return getMetadata(imageRef);
      })
      .then((meta) => setMetadata(meta))
      .catch((err) => {
        console.error("Failed to reload screenshot or metadata:", err);
        setError("Screenshot not available");
      })
      .finally(() => {
        setImgLading(false);
        setIsRefreshing(false);
      });
  }, [data?.serial_name]);

  useEffect(() => {
    refetchImg();
  }, [realtimeData?.screenShotTime, refetchImg]);

  const screenShot = useCallback(async (deviceID, action) => {
    const serial = data?.serial_name;
    setImgLading(true);
    setIsRefreshing(true);
    try {
      // Record the current screenshot's upload time BEFORE sending the action,
      // then poll until the device uploads a newer one. Refetching immediately
      // (the old behaviour) just re-showed the stale image, so it only updated
      // after a manual page refresh.
      const baseline = await getScreenshotBaseline(serial);
      console.log("[screenshot] inside: sending action for device", deviceID, "serial", serial);
      await deviceAction.mutateAsync({
        id: deviceID,
        data: { action: action.toLowerCase() }
      });
      const result = await pollForNewScreenshot(serial, baseline);
      console.log("[screenshot] inside: poll result", result ? "got new image" : "timed out");
      if (result) {
        setImageUrl(result.url);
        setMetadata(result.metadata);
        setError(null);
      } else {
        refetchImg(); // timed out — fall back to whatever exists
      }
    } catch (error) {
      console.error("Screenshot action failed:", error);
    } finally {
      setImgLading(false);
      setIsRefreshing(false);
    }
  }, [deviceAction, data?.serial_name, refetchImg]);

  useEffect(() => {
    if (typeof realtimeData?.app_version === "string" && realtimeData.app_version !== appVersion) {
      setAppVersion(realtimeData.app_version);
    }
  }, [realtimeData?.app_version, appVersion]);

  useEffect(() => {
    const incoming = realtimeData?.media_playing;
    const current = currentMediaPlaying;
    const hasMediaIdChanged = typeof incoming?.currentMediaId === "string" && incoming?.currentMediaId !== current?.currentMediaId;
    const hasStartTimeChanged = incoming?.start_time !== current?.start_time;
    const hasPauseTimeChanged = moment(incoming?.pause_start_time).valueOf() !== moment(current?.pause_start_time).valueOf();

    if (hasPauseTimeChanged) {
      const isPaused = !!incoming?.pause_start_time;
      dispatch(setPaused(isPaused));
    }
    if (hasMediaIdChanged || hasStartTimeChanged || hasPauseTimeChanged) {
      setCurrentMediaPlaying(incoming);
    }
    if (typeof realtimeData?.id === "string" && realtimeData.id.length > 0 && realtimeData.id !== currentCampaignId) {
      setCurrentCampaignId(realtimeData.id);
    }
  }, [realtimeData?.media_playing, realtimeData?.id, currentBannerPlaying, currentMediaPlaying, currentCampaignId, dispatch]);

  useEffect(() => {
    const incoming = realtimeData?.banner;
    const current = currentBannerPlaying;
    if (typeof incoming?.id === "string" && (incoming?.id !== current?.id || incoming?.start_time !== current?.start_time)) {
      setCurrentBannerPlaying(incoming);
    }
  }, [realtimeData?.banner, currentBannerPlaying]);

  useEffect(() => {
    const incoming = realtimeData?.playground;
    const current = currentPlaygroundPlaying;
    if (typeof incoming?.id === "string" && (incoming?.id !== current?.id || incoming?.start_time !== current?.start_time)) {
      setCurrentPlaygroundPlaying(incoming);
    }
  }, [realtimeData?.playground, currentPlaygroundPlaying]);

  useEffect(() => {
    const incoming = realtimeData?.corner;
    const current = currentCornerPlaying;
    if (typeof incoming?.id === "string" && (incoming?.id !== current?.id || incoming?.start_time !== current?.start_time)) {
      setCurrentCornerPlaying(incoming);
    }
  }, [realtimeData?.corner, currentCornerPlaying]);

  // ── Merged screen page: health = ONE truth from the backend (same presence function as the Screens list) ──
  const c = health.data;
  const tz = c?.device?.timezone;
  const [ops, setOps] = useState({});
  const [opFailed, setOpFailed] = useState({});
  const refreshHealth = useCallback(() => {
    queryClient.invalidateQueries(['screenHealth', id]);
    queryClient.invalidateQueries(['screenHealthFleet']);
    queryClient.invalidateQueries('getDevice');
  }, [queryClient, id]);
  const setOp = useCallback(async (kind, to) => {
    if (to === 'ask' || to === 'idle') {
      setOps((o) => ({ ...o, [kind]: to === 'idle' ? undefined : to }));
      setOpFailed((f) => ({ ...f, [kind]: false }));
      return;
    }
    const token = selectedDevice?.fcm_token;
    try {
      if (kind === 'restart') await restartDevice.mutateAsync({ id });
      else if (kind === 'off') await turnOnOffDevice.mutateAsync({ token, data: { action: 'turn_off' } });
      else if (kind === 'device') await turnOnOffDevice.mutateAsync({ token, data: { action: 'restart_device' } }); // the EXISTING whole-device restart (POST /device/message)
      else if (kind === 'on' || kind === 'start') await turnOnOffDevice.mutateAsync({ token, data: { action: 'turn_on' } });
      else if (kind === 'redl') await turnOnOffDevice.mutateAsync({ token, data: { action: 'flush' } });
      // build 396: the EXISTING play (resume) and mute/unmute endpoints -- the same ones the Screens card uses
      else if (kind === 'resume') await deviceAction.mutateAsync({ id, data: { action: 'play' } });
      else if (kind === 'mute' || kind === 'unmute') await muteUnmuteDevice.mutateAsync({ id, data: { action: kind } });
      setOps((o) => ({ ...o, [kind]: 'sent' }));
      setTimeout(refreshHealth, 4000);
    } catch (_e) {
      setOpFailed((f) => ({ ...f, [kind]: true }));
    }
  }, [id, restartDevice, turnOnOffDevice, deviceAction, muteUnmuteDevice, selectedDevice?.fcm_token, refreshHealth]);

  const settingsObj = selectedDevice?.device_settings || data?.device_settings || {};
  const hoursText = [
    hoursWindow(settingsObj)
      ? t('sh.hours_range', hoursWindow(settingsObj))
      : t('sh.hours_disabled'),
    settingsObj.dailyRestart ? t('sh.daily_restart_on', { time: settingsObj.dailyRestartTime || '04:00' }) : t('sh.daily_restart_off'),
  ].join(' · ');
  const statusTitle = c ? titleText(t, c.hero?.title, tz) : '';
  const supportVars = {
    screen_name: c?.device?.name || data?.name, company: c?.company_name, branch: c?.device?.branch || '',
    status: statusTitle, screen_id: c?.device?.serial || data?.serial_name || id, time: clock(new Date().toISOString(), tz),
  };
  const detailRows = [
    ...(data?.group_name ? [{ k: t('sh.info_group'), v: data.group_name }] : []),
    ...(c?.info?.picture_4k ? [{ k: t('sh.info_picture'), v: <span className="pp-sp-b4k">4K</span> }] : []),
    ...((c?.info?.app_version || appVersion) ? [{ k: t('sh.info_app'), v: <><span className="pp-sp-ltr pp-sp-tnum">{c?.info?.app_version || appVersion}</span>{c?.info ? ` · ${c.info.app_outdated ? t('sh.app_old') : t('sh.app_latest')}` : ''}</> }] : []),
    { k: t('sh.info_location'), v: geoLabel || t('sh.info_location_none') },
  ];
  const rotation = <Rotate degree={degree} setDegree={setDegree} />;

  const previewNode = (
                <div className="w-full">
                <div className="relative group w-full flex items-center justify-center">
                    <div 
                      className="relative bg-black rounded-[2.5rem] overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.4),0_30px_60px_-30px_rgba(0,0,0,0.5)] ring-[12px] ring-slate-900 transition-all duration-700 ease-in-out transform group-hover:scale-[1.002] mx-auto" 
                      style={{ 
                        aspectRatio: (Number(degree) === 90 || Number(degree) === 270) ? '9/16' : '16/9',
                        width: (Number(degree) === 90 || Number(degree) === 270) ? 'auto' : '100%',
                        height: (Number(degree) === 90 || Number(degree) === 270) ? 'min(80vh, 850px)' : 'auto',
                        maxHeight: (Number(degree) === 90 || Number(degree) === 270) ? '850px' : 'none',
                        maxWidth: '1100px',
                      }}
                    >

                      <div 
                        className="absolute inset-0 w-full h-full bg-black flex items-center justify-center transition-all duration-700 ease-in-out"
                        style={{ 
                          width: '100%', 
                          height: '100%',
                          left: '0',
                          top: '0',
                        }}
                      >
                        <div className="w-full h-full overflow-hidden flex items-center justify-center">
                          {activeTab === "preview" ? (
                            <ContentPreview
                              campaignData={campaignData}
                              currentBannerPlaying={currentBannerPlaying}
                              currentPlaygroundPlaying={currentPlaygroundPlaying}
                              currentCornerPlaying={currentCornerPlaying}
                              currentMediaPlaying={currentMediaPlaying}
                              campaignId={currentCampaignId}
                              deviceId={id}
                            />
                          ) : activeTab === "screenshot" ? (
                            imageUrl ? (
                              <div className="relative w-full h-full flex justify-center items-center">
                                {metadata && typeof metadata === "object" && (
                                  <div className="absolute top-6 right-8 group z-30">
                                    <div className="bg-white/20 backdrop-blur-md w-10 h-10 rounded-full flex items-center justify-center cursor-pointer hover:bg-white/40 transition-all border border-white/30 text-white shadow-lg">
                                      <i className="fa-solid fa-circle-info fa-lg"></i>
                                    </div>
                                    <div className="invisible group-hover:visible absolute right-0 mt-3 w-72 bg-white/95 backdrop-blur-xl border border-white/50 rounded-2xl shadow-2xl p-4 text-sm text-slate-700 z-40 transition-all transform scale-95 group-hover:scale-100 opacity-0 group-hover:opacity-100">
                                      <h4 className="font-bold mb-3 text-indigo-600 border-b border-indigo-50 pb-2">{t('devices.group_file_intelligence')}</h4>
                                      <div className="space-y-2">
                                        <div className="flex justify-between"><span className="text-slate-400">{t('devices.group_captured')}:</span> <span className="font-semibold">{metadata.timeCreated ? moment(metadata.timeCreated).format('LLL') : "Unknown"}</span></div>
                                        <div className="flex justify-between"><span className="text-slate-400">{t('devices.group_format')}:</span> <span className="font-semibold uppercase">{metadata.contentType?.split('/')[1] || "PNG"}</span></div>
                                        <div className="flex justify-between"><span className="text-slate-400">{t('devices.group_resolution')}:</span> <span className="font-semibold">HD+ (1080p)</span></div>
                                        <div className="flex justify-between"><span className="text-slate-400">{t('devices.group_storage')}:</span> <span className="font-semibold">{typeof metadata.size === "number" ? `${(metadata.size / 1024).toFixed(1)} KB` : "0 KB"}</span></div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                <div className="absolute bottom-6 right-8 z-30">
                                  <button
                                    className={`px-6 py-2.5 rounded-full font-bold flex items-center gap-2 shadow-xl transition-all active:scale-95 ${isRefreshing || imgLading ? 'bg-slate-700 text-slate-400' : 'bg-[var(--pp-btn-honey)] hover:bg-[var(--pp-btn-honey-hover)] text-white hover:shadow-[color-mix(in_srgb,var(--pp-btn-honey)_40%,transparent)]'}`}
                                    onClick={() => screenShot(id, "screenshot")}
                                    disabled={isRefreshing || imgLading}
                                  >
                                    {(isRefreshing || imgLading) ? <i className="pi pi-spin pi-spinner"></i> : <i className="pi pi-camera"></i>}
                                    {t('devices.group_capture_new')}
                                  </button>
                                </div>
                                <img src={imageUrl} alt={t('devices.group_screenshot')} className="w-full h-full object-contain bg-slate-900" />
                                {(isRefreshing || imgLading) && (
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-[2px] z-20">
                                    <div className="flex flex-col items-center gap-4">
                                      <ProgressSpinner style={{ width: "60px", height: "60px" }} strokeWidth="3" animationDuration="1.5s" />
                                      <span className="text-white font-bold tracking-widest uppercase text-xs">{t('devices.group_capturing_hardware')}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="flex flex-col items-center gap-4 text-slate-500">
                                <i className="bx bx-image-alt text-6xl opacity-20"></i>
                                <h2 className="text-xl font-medium">{error || t('devices.group_no_visual_data')}</h2>
                              </div>
                            )
                          ) : (
                            <div className="flex flex-col items-center gap-4 text-slate-500">
                              <i className="bx bx-broadcast text-6xl opacity-20 animate-pulse"></i>
                              <h2 className="text-xl font-medium tracking-wide">{t('devices.group_live_transmission_offline')}</h2>
                            </div>
                          )}
                        </div>
                      </div>

                      {activeTab === "preview" && (
                        <div className="absolute inset-0 w-full h-full z-50 pointer-events-none">
                          {/* Phase 3: gated on deviceStatus, not the isOnline
                              reachability boolean — standby/app_closed are
                              now isOnline=true (still reachable), so the
                              old `!isOnline &&` guard here would never fire
                              for them again and these takeovers would stop
                              showing entirely. */}
                          {deviceStatus === 'standby' && <div className="pointer-events-auto w-full h-full"><StandbyScreen /></div>}
                          {deviceStatus === 'app_closed' && <div className="pointer-events-auto w-full h-full"><AppClosedScreen /></div>}
                          {(deviceStatus === 'offline' || deviceStatus === 'scheduled_off') && <div className="pointer-events-auto w-full h-full"><OfflineScreen /></div>}
                          {deviceStatus === 'online' && realtimeData.name === "Prayer break" && (
                            <div className="absolute inset-0 z-40 pointer-events-auto">
                              <BlackoutScreen message={t('devices.status_prayer_break')} />
                            </div>
                          )}
                          {deviceStatus === 'online' && realtimeData.name === "Downloading..." && (
                            <div className="absolute inset-0 z-40 pointer-events-auto">
                              <GrayScreen message={t('devices.status_downloading_content')} />
                            </div>
                          )}
                          {deviceStatus === 'online' && campaignLoading && (
                            <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-black/40 backdrop-blur-sm pointer-events-auto">
                              <div className="flex flex-col items-center gap-4 text-white">
                                <ProgressSpinner style={{ width: "50px", height: "50px" }} strokeWidth="3" />
                                <span className="text-sm font-medium animate-pulse uppercase tracking-widest">{t('devices.status_hydrating_campaign')}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
              </div>
  );

  const geoNode = (
<section className="pp-sp-panel" data-testid="sh-geo" style={{ gap: 6 }}>
                <EffectiveTimezone
                  timezone={selectedDevice?.resolved_timezone}
                  source={selectedDevice?.timezone_source}
                  sourceName={selectedDevice?.timezone_source_name}
                />
                {clockIssue?.kind === 'mismatch' && (
                  <div className="pp-sp-note" style={{ fontSize: 13, background: 'var(--pp-comp-warn-soft)', border: '1px solid var(--pp-comp-warn)', borderRadius: 10, padding: '10px 12px' }}>
                    <b style={{ display: 'block' }}>{t('devices.tz_mismatch_title', "This screen's clock doesn't match its venue")}</b>
                    <span>{t('devices.tz_mismatch_body', {
                      deviceZone: clockIssue.deviceZone, deviceOffset: clockIssue.deviceOffsetLabel,
                      venueZone: clockIssue.venueZone, venueOffset: clockIssue.venueOffsetLabel,
                      drift: tzDriftPhrase(clockIssue.deltaMinutes, t),
                      defaultValue: "Clock is set to {{deviceZone}} ({{deviceOffset}}) but the venue is {{venueZone}} ({{venueOffset}}). Schedules will fire {{drift}}.",
                    })}</span>
                    <span style={{ display: 'block', color: 'var(--pp-comp-muted)' }}>{t('devices.tz_mismatch_fix', 'Fix it on the screen: Android Settings → Date & time → set the timezone, then restart the app.')}</span>
                  </div>
                )}
              </section>
  );

  return (
    <>
      <Layout>
        <MergedScreen
          logDay={logDay} onLogDay={setLogDay} dayLog={dayLog}
          t={t} lang={lang} c={c} loading={health.isLoading} loadFailed={health.isError} onRetry={() => health.refetch()}
          name={selectedDevice?.name || data?.name} serial={selectedDevice?.serial_name} onBack={() => navigate('/devices/list')}
          ops={ops} setOp={setOp} failed={opFailed} supportVars={supportVars}
          activeTab={activeTab} setActiveTab={setActiveTab} preview={previewNode} campaignName={assignedCampaignName}
          onAssign={openAssignModal} onSettings={() => dispatch(toggleDialog("deviceSettings"))}
          rotation={rotation} hoursText={hoursText} detailRows={detailRows} geo={geoNode}
          muted={resolveMuted(realtimeData?.is_muted, c?.muted)} canMute
          pausedNode={data?.schedule_paused ? <SchedulePausedChip paused={data.schedule_paused} deviceId={data.id || id} tz={data.resolved_timezone || selectedDevice?.resolved_timezone} /> : null}
          canPower={!!selectedDevice?.fcm_token} perfEnabled={!!selectedDevice?.ai_analytics}
          onPerf={() => { if (selectedDevice?.ai_analytics) dispatch(toggleDialog("analyticsDialog")); }}
        />
      </Layout>
      {openDialog.assignCampaignsDropDown && (<AssignCampaignsDropDown clearDeviceOnClose={false} />)}
      {openDialog.analyticsDialog && (<AnalyticsDialog />)}
      {openDialog.deviceSettings && (<DeviceSettings />)}
      <FirebaseDeviceListener setRealtimeData={setRealtimeData} path={`Devices/${selectedDevice.serial_name}/DeviceState`} />
      <DeviceLastLoginListener serialName={selectedDevice.serial_name} path={`Devices/${selectedDevice.serial_name}/presence`} />
    </>
  );
};

export default DeviceShow;
